import React from 'react';

import { gql } from '@apollo/client';

import { ActivityLogTaskFlagLifecycleResolved_ActivityLogFragment, ActivityType } from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import ActivityLogBase from '../../Helpers/ActivityLogBase';

interface Fragments {
  activityLog: ActivityLogTaskFlagLifecycleResolved_ActivityLogFragment;
}

type ActionLabel = {
  key: string;
  values?: Record<string, unknown>;
};

const getActionLabel = (activityLog: ActivityLogTaskFlagLifecycleResolved_ActivityLogFragment): ActionLabel => {
  const { activityType, details } = activityLog;

  if (activityType === ActivityType.TaskFlagResolvedCandidateTentative) {
    return { key: 'hiring-dashboard-candidates:candidates.activityLog.resolvedCandidateTentative' };
  }

  if (details?.__typename !== 'ActivityLogTaskInterviewerDetails') {
    return {
      key: 'hiring-dashboard-candidates:candidates.activityLog.resolvedFlag',
    };
  }

  const values = {
    name: details.interviewer.fullName,
    eventName: details.interviewName,
  };

  switch (activityType) {
    case ActivityType.TaskFlagResolvedInterviewerTentative:
      return {
        key: 'hiring-dashboard-candidates:candidates.activityLog.resolvedInterviewerTentative',
        values,
      };
    case ActivityType.TaskFlagResolvedInterviewerDeactivated:
      return {
        key: 'hiring-dashboard-candidates:candidates.activityLog.resolvedInterviewerDeactivated',
        values,
      };
    case ActivityType.TaskFlagResolvedDebriefAttendeeTentative:
      return {
        key: 'hiring-dashboard-candidates:candidates.activityLog.resolvedDebriefAttendeeTentative',
        values,
      };
    case ActivityType.TaskFlagResolvedDebriefAttendeeDeactivated:
      return {
        key: 'hiring-dashboard-candidates:candidates.activityLog.resolvedDebriefAttendeeDeactivated',
        values,
      };
    default:
      return { key: 'hiring-dashboard-candidates:candidates.activityLog.resolvedFlag' };
  }
};

/** Renders the five lifecycle flag resolution events with schema-backed context. */
const ActivityLogTaskFlagLifecycleResolved: FCWithFragments<Fragments> = ({ activityLog }) => {
  const actionLabel = getActionLabel(activityLog);

  return (
    <ActivityLogBase
      activityLog={activityLog}
      actionLabel={activityLog.plaintext ?? undefined}
      actionLabelKey={actionLabel.key}
      actionLabelValues={actionLabel.values}
    />
  );
};

ActivityLogTaskFlagLifecycleResolved.fragments = {
  activityLog: gql`
    ${ActivityLogBase.fragments.activityLog}
    fragment ActivityLogTaskFlagLifecycleResolved_activityLog on ActivityLog {
      id
      activityType
      plaintext
      details {
        ... on ActivityLogTaskInterviewerDetails {
          __typename
          interviewName
          interviewer {
            id
            fullName
          }
        }
      }
      ...ActivityLogBase_activityLog
    }
  `,
};

export default ActivityLogTaskFlagLifecycleResolved;
